import { AnalyticsService } from './analytics.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Inject, Injectable, Injector, PLATFORM_ID } from '@angular/core';
// import * as stopword from 'stopword';
import { removeStopwords } from 'stopword';

import { take, map } from 'rxjs/operators';
import Fuse from 'fuse.js';
import * as retailSearch from './../json/retail-search.json';
import { isPlatformBrowser } from '@angular/common';
import { increment, limit, serverTimestamp, where } from '@angular/fire/firestore';
import {
  ensureInitialized,
  fetchAndActivate,
  getValue,
  RemoteConfig
} from '@angular/fire/remote-config';
import { DbService } from './db.service';

@Injectable({
  providedIn: 'root'
})
export class HelpService {
  private _db = inject(DbService);
  // Resolved lazily (see getRemoteConfigData): the RemoteConfig DI token runs an
  // isSupported() gate that throws "isSupported() ... has not resolved" if injected
  // before AngularFire's async probe settles. Eagerly field-injecting it here threw
  // during HelpService construction, which in turn threw while constructing
  // ShopService (a consumer) — poisoning ShopService's root DI record so later
  // injections returned an empty object and surfaced as "activeCart$ is not a function".
  private _injector = inject(Injector);

  HTTP_URL = 'https://us-east4-russells-web.cloudfunctions.net/contact/';

  themeItems = [];
  themeGalleryURL = '';

  _siteSearch: any = null;
  _siteContent = retailSearch as any;

  _remoteConfigInitialized = false;
  isBrowser = false;

  constructor(
    private http: HttpClient,
    private analytics: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  async searchFaq(input: string): Promise<any> {
    let cleaned = this.sterilizeSearch(input);
    await this.analytics.logEvent('search_faq', {
      question: input.toLowerCase(),
      terms: cleaned
    });
    if (cleaned.length < 1) {
      cleaned = input.split(' ');
    }
    return this._db
      .queryCollection$('faqs', [where('searchTerms', 'array-contains-any', cleaned), limit(5)])
      .pipe(
        take(1),
        map((actions) => {
          return actions.map((a) => {
            return a;
          });
        })
      )
      .toPromise();
  }

  async upVoteFaq(faq: any): Promise<void> {
    return this._db.updateDoc('faqs', faq.id, {
      upVotes: increment(1),
      updatedAt: serverTimestamp()
    });
  }

  private sterilizeSearch(searchTerm: string): string[] {
    const regex = /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g;
    return removeStopwords(searchTerm.toLowerCase().replace(regex, '').split(' '));
  }

  async sendContactForm(formData): Promise<string> {
    const resp = (await this.http
      .post(
        `${this.HTTP_URL}contact`,
        { ...formData, createdAt: new Date(), updatedAt: new Date() },
        this.getDefaultHttpOptions()
      )
      .toPromise()) as any;
    await this.analytics.logEvent('submit_contact_form', {
      contactFormType: formData.type,
      message: formData.message
    });
    return resp.message;
  }

  private getDefaultHttpOptions(): any {
    let headers = new HttpHeaders();
    headers = headers.append('Accept', 'application/json');
    headers = headers.append('Content-Type', 'application/json; charset=utf-8');

    const httpOptions = {
      headers
    };
    return httpOptions;
  }

  async getRemoteConfigData(key: string, fromDB = false): Promise<string> {
    // Remote Config has a client-side cache (minimumFetchIntervalMillis, default 12h) that can
    // delay propagation of updates. Pass `fromFirestore = true` to bypass the cache and read the
    // mirrored value directly from `remote-configs/MAIN` (matches the CMS ConfigService pattern).
    if (fromDB) {
      const conf = await this._db.readDoc<any>('remote-configs', 'MAIN');
      return conf?.parameters?.[key]?.defaultValue?.value ?? '';
    }
    if (this.isBrowser) {
      const remoteConfig = this._injector.get(RemoteConfig);
      if (!this._remoteConfigInitialized) {
        await ensureInitialized(remoteConfig);
        await fetchAndActivate(remoteConfig);
        this._remoteConfigInitialized = true;
      }

      const val = getValue(remoteConfig, key);
      return val ? val.asString() : '';
    }
    return '';
  }

  async getSiteSearchResults(searchTerms: string, maxScore = 0.3): Promise<any[]> {
    let fuse = this._siteSearch;
    if (!fuse) {
      this._siteSearch = new Fuse(this._siteContent.default.data, {
        includeScore: true,
        includeMatches: true,
        findAllMatches: false,
        ignoreLocation: true,
        isCaseSensitive: false,
        shouldSort: true,
        keys: [
          'text',
          { name: 'h1', weight: 0.9 },
          { name: 'metaTitle', weight: 0.9 },
          { name: 'metaDescription', weight: 0.85 },
          { name: 'h2', weight: 0.75 },
          { name: 'h3', weight: 0.5 }
        ]
      });
      fuse = this._siteSearch;
    }
    const term = this.sterilizeSearch(searchTerms).join(' ');
    const res: any[] = fuse.search(term) || [];
    return res.filter((r) => {
      if (r.score < maxScore) {
        return true;
      }
      return false;
    });
  }
}
